/**
 * In-app browser detection (KakaoTalk, Zalo, Facebook, Messenger, Instagram, LINE, NAVER app,
 * NAVER BAND) and the "open in the device browser" action. Links shared in messengers open in the
 * app's own WebView, where PWA install, durable IndexedDB (the caddy offline queue) and App Links
 * behave badly.
 *
 * - KakaoTalk: on the shared entry pages (/t /p /r /join /c) the page leaves once per session,
 *   automatically, through Kakao's documented `kakaotalk://web/openExternal` scheme (skipped with
 *   `?ext=0`, in TV mode, on admin pages and while this WebView holds a scorer session or unsent
 *   caddy ops); the banner (components/in-app-banner) is the fallback.
 * - Every other in-app browser: the banner only, never automatic.
 *
 * The same detection table is used by the demo (docs/demo/engine/inapp.js `PD.inapp.APPS`); keep
 * the two in step (ids, order, iPadOS check).
 */
import { EMBED_UA_TOKEN } from '@pingo/shared';

/** the PinGo app's own in-app web screen (§15 #402) is not a messenger WebView to escape */
const EMBED_UA_RE = new RegExp(`\\b${EMBED_UA_TOKEN}/`);

export type InAppKind =
  'kakaotalk' | 'zalo' | 'facebook' | 'messenger' | 'instagram' | 'line' | 'naver' | 'band';
export type InAppOs = 'android' | 'ios';

export interface InAppBrowser {
  kind: InAppKind;
  os: InAppOs;
}

/**
 * First match wins. Order matters: Instagram and Messenger WebViews also carry Facebook tokens
 * (FBAN/FBAV) on some builds, so the more specific apps are checked first.
 */
export const IN_APP_RULES: readonly { kind: InAppKind; test: RegExp }[] = [
  { kind: 'kakaotalk', test: /KAKAOTALK/i },
  { kind: 'zalo', test: /Zalo/i },
  { kind: 'instagram', test: /Instagram/i },
  { kind: 'messenger', test: /Messenger/i },
  { kind: 'facebook', test: /FBAN|FBAV|FB_IAB|FBIOS/i },
  { kind: 'line', test: /\bLine\//i },
  { kind: 'naver', test: /NAVER\(inapp/i },
  // NAVER BAND (Korean golf clubs): "… BAND/14.1.0 (…)"; case-sensitive so "band/" in a URL-ish
  // token or "Broadband/" never matches
  { kind: 'band', test: /\bBAND\// },
];

/** Display names (brand names, the same in every language). */
export const IN_APP_NAMES: Record<InAppKind, string> = {
  kakaotalk: 'KakaoTalk',
  zalo: 'Zalo',
  facebook: 'Facebook',
  messenger: 'Messenger',
  instagram: 'Instagram',
  line: 'LINE',
  naver: 'NAVER',
  band: 'BAND',
};

/**
 * `maxTouchPoints` = navigator.maxTouchPoints: iPadOS reports a desktop Mac user agent, told
 * apart from a real Mac by its touch screen.
 */
export function detectOs(ua: string, maxTouchPoints = 0): InAppOs | null {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  return null;
}

/** The in-app browser the page runs in, or null (regular browser, desktop, unknown platform). */
export function detectInAppBrowser(
  ua: string | null | undefined,
  maxTouchPoints = 0,
): InAppBrowser | null {
  if (!ua || EMBED_UA_RE.test(ua)) return null;
  const os = detectOs(ua, maxTouchPoints);
  if (!os) return null; // desktop messengers open links in the default browser already
  const rule = IN_APP_RULES.find((r) => r.test.test(ua));
  return rule ? { kind: rule.kind, os } : null;
}

/** KakaoTalk's documented scheme: opens the URL in the device's default browser (Android, iOS). */
export function kakaoExternalUrl(url: string): string {
  return `kakaotalk://web/openExternal?url=${encodeURIComponent(url)}`;
}

/**
 * LINE opens a URL with `openExternalBrowser=1` in the device's default browser (Android, iOS).
 * The extra query key is ignored by the web routes and the api.
 */
export function lineExternalUrl(url: string): string {
  const u = new URL(url);
  u.searchParams.set('openExternalBrowser', '1');
  return u.toString();
}

/**
 * Android Chrome intent; `browser_fallback_url` keeps the user on this page when Chrome is not
 * installed. Only http(s) URLs are converted.
 *
 * `package=com.android.chrome` is deliberate: the user asked for these links to open in Chrome
 * ("open KakaoTalk links in Chrome"). The cost: a phone whose default is Samsung Internet still gets Chrome, and
 * an installed PinGo app is not offered (its App Link) from here — the native app is reached from
 * the browser or a QR scan instead.
 *
 * Not yet checked on devices: whether every host WebView (Zalo, Facebook, Messenger, Instagram,
 * NAVER, BAND on Android) hands `intent://` to the system. One that does not shows its own
 * "unknown URL scheme" page; the banner therefore opens it only on a tap (never automatically) and
 * shows the menu steps and the copy-link fallback at the same time.
 */
export function androidChromeIntent(url: string): string | null {
  const u = new URL(url);
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const scheme = u.protocol.slice(0, -1);
  return (
    `intent://${u.host}${u.pathname}${u.search}${u.hash}` +
    `#Intent;scheme=${scheme};package=com.android.chrome;` +
    `S.browser_fallback_url=${encodeURIComponent(u.toString())};end`
  );
}

export type ExternalOpen =
  /** navigate to this href (scheme / intent) — the messenger hands it to the browser */
  | { type: 'href'; href: string }
  /** no reliable scheme (iOS Facebook, Messenger, Instagram, Zalo, NAVER, BAND): menu steps */
  | { type: 'instructions' };

/** How "Open in browser" works for this in-app browser and page URL. */
export function externalOpen(browser: InAppBrowser, url: string): ExternalOpen {
  if (browser.kind === 'kakaotalk') return { type: 'href', href: kakaoExternalUrl(url) };
  if (browser.kind === 'line') return { type: 'href', href: lineExternalUrl(url) };
  if (browser.os === 'android') {
    const href = androidChromeIntent(url);
    if (href) return { type: 'href', href };
  }
  return { type: 'instructions' };
}

/** Pages where the banner must never appear (TV broadcast mode is a big screen, not a phone). */
export function isInAppBannerExcluded(pathname: string): boolean {
  return /^\/t\/[^/]+\/tv\/?$/.test(pathname);
}

/** Caddy entry pages: score entry relies on durable offline storage, so the wording is stronger. */
export function isCaddyPath(pathname: string): boolean {
  return /^\/c\/[^/]+\/?$/.test(pathname) || /^\/caddy(\/|$)/.test(pathname);
}

export const IN_APP_DISMISS_KEY = 'pingo.inApp.dismissed';

/**
 * `?ext=0` opts out of the automatic KakaoTalk exit and the banner (e.g. links printed for a venue
 * that knows its WebView).
 */
export function hasOptOut(search: string): boolean {
  return new URLSearchParams(search).get('ext') === '0';
}

export function isInAppDismissed(): boolean {
  try {
    return sessionStorage.getItem(IN_APP_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function rememberInAppDismissed(): void {
  try {
    sessionStorage.setItem(IN_APP_DISMISS_KEY, '1');
  } catch {
    // storage blocked (private mode / WebView settings): dismissal lasts for this page view only
  }
}

/**
 * Shared entry pages where KakaoTalk leaves automatically: public leaderboard pages (not TV),
 * caddy login, player portal, registration and club invites. Never the admin console (/a/, /admin),
 * the logged-in caddy workspace (/caddy) or anything else.
 */
export function isKakaoAutoExitPath(pathname: string): boolean {
  if (isInAppBannerExcluded(pathname)) return false;
  return (
    /^\/t\/[^/]+(\/(contests|matches|groups|scorecard\/[^/]+))?\/?$/.test(pathname) ||
    /^\/(c|p|r|join)\/[^/]+\/?$/.test(pathname)
  );
}

/** ms the banner waits after the automatic KakaoTalk exit (redirect blocked, or the user came back) */
export const KAKAO_EXIT_WAIT_MS = 1500;
export const KAKAO_EXIT_KEY = 'pingo.inApp.kakaoExit';

/** once per page load even when sessionStorage is blocked */
let kakaoExitThisPage = false;

export function kakaoExitTried(): boolean {
  if (kakaoExitThisPage) return true;
  try {
    return sessionStorage.getItem(KAKAO_EXIT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markKakaoExitTried(): void {
  kakaoExitThisPage = true;
  try {
    sessionStorage.setItem(KAKAO_EXIT_KEY, '1');
  } catch {
    // storage blocked: the page-load flag above still prevents a loop on this page
  }
}

/** tests only: forget the page-load flag */
export function resetInAppPageMemory(): void {
  kakaoExitThisPage = false;
}

/**
 * Whether to try the automatic KakaoTalk exit now (pure part; the caller adds the scorer checks:
 * no CADDY / SELF_ENTRY session in this WebView and no unsent caddy ops).
 */
export function canAutoExit(browser: InAppBrowser | null, pathname: string, search: string) {
  return (
    browser?.kind === 'kakaotalk' &&
    isKakaoAutoExitPath(pathname) &&
    !hasOptOut(search) &&
    !isInAppDismissed() &&
    !kakaoExitTried()
  );
}
